import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-empty-state.css?inline';

/**
 * `<pk-empty-state>` — empty-state placeholder block.
 *
 * Used on surfaces that have no rows yet (e.g. "No backups yet", "No invites
 * pending", "No users match this filter"). Renders a centered icon + heading
 * + lede with an optional CTA button area.
 *
 * Slots:
 *   - `icon`   — leading glyph (SVG or `<pk-icon-button>` etc.); defaults to a
 *                neutral filler dot rendered by the shadow DOM. Pass any element
 *                slotted into the `icon` slot to override.
 *   - `cta`    — call-to-action area (typically a `<pk-button>`). Empty by default.
 *
 * Attributes:
 *   - `heading` — bold title text shown beneath the icon.
 *   - `lede`    — supporting copy beneath the heading.
 *   - `compact` — boolean; when present, reduces padding for use inside cards.
 *
 * Events: none.
 */
export class PkEmptyState extends HTMLElement {
    static readonly tagName = 'pk-empty-state';

    static get observedAttributes(): string[] {
        return ['heading', 'lede', 'compact'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private template(): TemplateResult {
        const heading = this.getAttribute('heading') ?? '';
        const lede    = this.getAttribute('lede')    ?? '';
        const compact = this.hasAttribute('compact');

        return html`
            <style>${styles}</style>
            <div class="empty-state ${compact ? 'compact' : ''}" role="status">
                <div class="icon-wrap">
                    <slot name="icon">
                        <span class="icon-default" aria-hidden="true"></span>
                    </slot>
                </div>
                ${heading ? html`<h2 class="heading">${heading}</h2>` : ''}
                ${lede    ? html`<p class="lede">${lede}</p>`        : ''}
                <div class="cta-wrap">
                    <slot name="cta"></slot>
                </div>
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkEmptyState.tagName)) {
    customElements.define(PkEmptyState.tagName, PkEmptyState);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-empty-state': PkEmptyState;
    }
}
